// The sticky control submits the existing Shopify form; it never builds its own cart payload.
export function stickyState({ variant, confirmed, quantity, busy, blocked, nativeOnly }) {
  if (!confirmed || !variant) return { action: 'choose', label: 'Choose an option', total: null };
  if (!Number.isSafeInteger(quantity) || quantity < 1) return { action: 'choose', label: 'Check quantity', total: null };
  if (nativeOnly) return { action: 'choose', label: 'View purchase options', total: null };
  const total = Number.isSafeInteger(variant.price) && variant.price >= 0 && Number.isSafeInteger(variant.price * quantity)
    ? variant.price * quantity : null;
  if (total === null) return { action: 'choose', label: 'View purchase options', total: null };
  if (busy) return { action: 'wait', label: 'Updating…', total };
  if (!variant.available) return { action: 'wait', label: 'Sold out', total };
  if (blocked) return { action: 'wait', label: blocked, total };
  return { action: 'buy', label: 'Add to cart', total };
}

if (typeof customElements !== 'undefined' && !customElements.get('ollaway-sticky-buy')) {
  customElements.define('ollaway-sticky-buy', class extends HTMLElement {
    connectedCallback() {
      this.source = document.getElementById(this.dataset.productSection);
      this.form = document.getElementById(this.dataset.formId);
      this.button = this.querySelector('[data-buy]');
      if (!this.source || !this.form || !this.button) return;
      try { this.variants = JSON.parse(this.querySelector('[data-variants]').textContent); }
      catch { return; }
      this.explicitVariant = new URL(location.href).searchParams.get('variant');
      this.touched = new Set();
      this.pending = false;
      this.inlineVisible = true;
      this.listen = new AbortController();
      const signal = this.listen.signal;
      this.source.addEventListener('click', event => {
        if (event.target.closest('variant-selects') && event.target.name) this.touched.add(event.target.name);
        queueMicrotask(() => this.sync());
      }, { signal });
      this.source.addEventListener('change', event => {
        if (event.target.closest('variant-selects')) {
          this.touched.add(event.target.name);
          this.pending = true;
        } else if (event.target.name === 'id') this.pending = false;
        queueMicrotask(() => this.sync());
      }, { signal });
      this.source.addEventListener('input', () => this.sync(), { signal });
      this.button.addEventListener('click', () => this.activate(), { signal });
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.source, { subtree: true, childList: true, attributes: true, characterData: true });
      const nativeButton = this.form.querySelector('[name="add"]');
      if (!nativeButton) return;
      this.visibility = new IntersectionObserver(entries => {
        this.inlineVisible = entries[0].isIntersecting;
        this.sync();
      }, { threshold: 0.25 });
      this.visibility.observe(nativeButton);
      this.size = new ResizeObserver(() => {
        if (!this.hidden) document.body.style.setProperty('--ollaway-sticky-height', `${this.getBoundingClientRect().height + 12}px`);
      });
      this.size.observe(this);
      this.sync();
    }
    sync() {
      const id = this.form.querySelector('[name="id"]')?.value;
      const variant = this.variants.find(item => String(item.id) === id);
      const groups = Array.from(this.source.querySelectorAll('variant-selects .product-form__input'));
      const confirmed = this.variants.length === 1 || this.explicitVariant === id ||
        (groups.length > 0 && groups.every(group => this.touched.has(group.querySelector('input, select')?.name)));
      const quantityInput = this.source.querySelector('[name="quantity"]');
      const quantity = quantityInput ? Number(quantityInput.value) : 1;
      const nativeButton = this.form.querySelector('[name="add"]');
      const busy = this.pending || nativeButton?.getAttribute('aria-disabled') === 'true';
      const blocked = nativeButton?.disabled ? nativeButton.textContent.trim() || 'Unavailable' : '';
      // ponytail: subscription/volume pricing stays in the native form until its full pricing model is needed here.
      this.state = stickyState({ variant, confirmed, quantity, busy, blocked, nativeOnly: this.dataset.nativeOnly === 'true' });
      if (quantityInput && !quantityInput.validity.valid) this.state = { action: 'choose', label: 'Check quantity', total: null };
      const error = this.source.querySelector('.product-form__error-message-wrapper:not([hidden]) .product-form__error-message')?.textContent.trim();
      if (error) this.state = { action: 'choose', label: 'Review product', total: this.state.total };
      this.querySelector('[data-pack]').textContent = confirmed && variant
        ? `${quantity > 1 ? `${quantity} × ` : ''}${variant.title === 'Default Title' ? this.source.querySelector('.product__title')?.textContent.trim() || 'Selected product' : variant.title}`
        : 'Choose your option';
      this.querySelector('[data-total]').textContent = this.state.total === null ? '' : new Intl.NumberFormat(this.dataset.locale || 'en', {
        style: 'currency', currency: this.dataset.currency || 'USD'
      }).format(this.state.total / 100);
      const comparison = this.querySelector('[data-compare]');
      const comparisonTotal = Number(variant?.compare_at_price) * quantity;
      comparison.hidden = !(this.state.total !== null && Number.isSafeInteger(comparisonTotal) && comparisonTotal > this.state.total);
      comparison.textContent = comparison.hidden ? '' : new Intl.NumberFormat(this.dataset.locale || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(comparisonTotal / 100);
      this.querySelector('[data-status]').textContent = error || (this.state.total !== null ? 'Item total · tax/shipping at checkout' : 'Select your option above');
      this.button.textContent = this.state.label;
      this.button.disabled = this.state.action === 'wait';
      this.hidden = this.inlineVisible;
    }
    activate() {
      this.sync();
      if (this.state.action === 'wait') return;
      if (this.state.action === 'choose') {
        const target = this.source.querySelector('variant-selects') || this.form;
        target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
        (target.querySelector('input:not([type="hidden"]), select, button') || this.form.querySelector('[name="add"]'))?.focus({ preventScroll: true });
        return;
      }
      const button = this.form.querySelector('[name="add"]');
      if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
      this.form.requestSubmit(button);
    }
    disconnectedCallback() {
      this.listen?.abort();
      this.observer?.disconnect();
      this.visibility?.disconnect();
      this.size?.disconnect();
    }
  });
}
